import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { FaTrash } from 'react-icons/fa';
import maverickPortalhttp from '../../utils/webService/webService';
import maverickPortalApiEndPoint from '../../config/apiEndPoint';
 
export const ImageUploadContainer = styled.div`
  margin-top: 1rem;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
`;
 
export const SuccessRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;
 
export const SuccessMessage = styled.span`
  color: #9EBE21;
  font-weight: 500;
  font-size: 13px;
  display: flex;
  align-items: center;
 
  @media (min-width: 600px) {
    font-size: 15px;
  }
`;
 
export const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #888;
  margin-left: 12px;
  display: flex;
  align-items: center;
 
  &:hover {
    color: #333;
  }
`;
 
export const UploadButtonLabel = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
  width: 100%;
`;
 
export const UploadButton = styled.button`
  background: #fff;
  color: #9EBE21;
  border: 2px solid #9EBE21;
  font-weight: bold;
  border-radius: 6px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  text-align: center;
 
  &:hover {
    background: #f6fbe9;
  }
`;

const ProgressCircle = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: #f0f0f0;
  position: relative;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #7c7c7c;
  font-weight: bold;
`;
 
const ImageUploadQuestion = ({
  question,
  qIdx,
  mediaUrls,
  setMediaUrls,
  selectedFiles,
  setSelectedFiles,
  handleAnswerChange,
  uploadImage,
  preAssignedImageUrl,
  setPreAssignedImageUrls,
  successMsg,
  disabled = false
}) => {
  const questionId = question.question_ID;
  const imageUrl = preAssignedImageUrl || mediaUrls[questionId];
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [retryStage, setRetryStage] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
 
  const handleDelete = async () => {
    if (!navigator.onLine) {
      alert('Network is down. Please try connecting to another network');
      return;
    }
    try {
      const user_ID = localStorage.getItem("user_id");
      const payload = { user_ID: user_ID, question_ID: questionId };
 
      await maverickPortalhttp.makeRequest(
        maverickPortalApiEndPoint.deleteMedia.url,
        maverickPortalApiEndPoint.deleteMedia.methodType,
        payload
      );
 
      setMediaUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[questionId];
        return newUrls;
      });
 
      setPreAssignedImageUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[questionId];
        return newUrls;
      });
 
      setSelectedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[questionId];
        return newFiles;
      });
 
      handleAnswerChange(questionId, null);
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete the image. Please try again.');
    }
  };
 
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    console.log('Selected file:', file);
 
    if (!file) {
      alert('No file selected. Try again.');
      return;
    }
 
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }
 
    setSelectedFiles(prev => ({ ...prev, [questionId]: file }));
    const url = URL.createObjectURL(file);
    setMediaUrls(prev => ({ ...prev, [questionId]: url }));
    setUploadProgress(0);
    setRetryStage(null);
    setErrorMessage('');
    setIsUploading(true);

    try {
      await uploadImage(
        file,
        questionId,
        question.questionType_ID,
        setUploadProgress,
        ({ attempt }) => {
          setRetryStage(attempt);
        }
      );
      setIsUploading(false);
      setRetryStage(null);
    } catch (error) {
      if (error?.imageAllRetriesFailed) {
        setRetryStage(3);
        setTimeout(() => {
          setIsUploading(false);
          setRetryStage(null);
          setErrorMessage('');
        }, 2500);
      } else {
        setIsUploading(false);
        setRetryStage(null);
        setErrorMessage('');
      }
    }
  };
 
  // ✅ This function opens gallery only
  const openGalleryPicker = () => {
    if (disabled) return;
 
    // Create a temporary input dynamically (bypasses Samsung issues)
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    // ❗ No capture attribute = only gallery
    input.style.display = 'none';
    document.body.appendChild(input);
 
    input.addEventListener('change', (e) => {
      handleFileChange(e);
      document.body.removeChild(input);
    });
 
    // Direct user gesture click
    input.click();
  };
 
  if (isUploading) {
    return (
      <ImageUploadContainer>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ marginBottom: '10px' }}>
            <ProgressCircle>{Math.round(uploadProgress * 100)}%</ProgressCircle>
          </div>
          {retryStage == null && <div style={{ color: '#9ebe21', fontWeight: 500 }}>Image uploading...</div>}
          {retryStage === 1 && <div style={{ color: 'red', fontWeight: 500 }}>Your connection is down...</div>}
          {retryStage === 2 && <div style={{ color: 'red', fontWeight: 500 }}>It is taking longer than usual...</div>}
          {retryStage === 3 && <div style={{ color: 'red', fontWeight: 500 }}>Image upload failed...</div>}
          {errorMessage && <div style={{ color: 'red', fontSize: '13px', marginTop: '10px' }}>{errorMessage}</div>}
        </div>
      </ImageUploadContainer>
    );
  }

  if (preAssignedImageUrl) {
    return (
      <ImageUploadContainer>
        <SuccessRow>
          <SuccessMessage>&#10003; Image uploaded successfully.</SuccessMessage>
          <DeleteButton
            onClick={disabled ? undefined : handleDelete}
            title="Delete image"
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <FaTrash />
          </DeleteButton>
        </SuccessRow>
      </ImageUploadContainer>
    );
  }
 
  return (
    <ImageUploadContainer>
      {imageUrl && !preAssignedImageUrl && (
        <SuccessRow>
          <SuccessMessage>&#10003; Image uploaded successfully.</SuccessMessage>
          <DeleteButton
            onClick={disabled ? undefined : handleDelete}
            title="Delete image"
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <FaTrash />
          </DeleteButton>
        </SuccessRow>
      )}
 
      {!imageUrl && (
        <UploadButtonLabel>
          <UploadButton
            type="button"
            onClick={openGalleryPicker}
            style={{
              backgroundColor: disabled ? '#f5f5f5' : '#fff',
              color: disabled ? '#aaa' : '#9EBE21',
              borderColor: disabled ? '#ddd' : '#9EBE21',
            }}
          >
            Choose Image
          </UploadButton>
        </UploadButtonLabel>
      )}
      {errorMessage && (
        <div style={{ color: 'red', fontSize: '13px', marginTop: '10px', textAlign: 'center' }}>
          {errorMessage}
        </div>
      )}
    </ImageUploadContainer>
  );
};
 
export default ImageUploadQuestion;